import { nf } from './consts'
import { Side, SIDE_LABEL, SIDE_UNIT } from './shortfallSeries'
import { ShortfallReason } from './types'
import { v2ReasonLabel } from '../../DepositPayoutTimeline/core/consts'

/**
 * Одна серия внутри тултипа: итог и разбивка по причинам. Секций две — депозиты
 * без выплаты и выплаты без депозита; заголовок, единица счёта и текст пустой
 * стороны берутся по `side`.
 */
export type TooltipSection = {
  side: Side
  total: { count: number; amount: number }
  reasons: ShortfallReason[]
}

const EMPTY_TEXT: Record<Side, string> = {
  deposit: 'нет депозитов без выплаты',
  payout: 'нет выплат без депозита',
}

/** Причины, которые пишет сам отчёт; коды отказов ядра подписывает `v2ReasonLabel`. */
const DEPOSIT_REASON_LABEL: Record<string, string> = {
  tiles_not_chosen: 'плитки предложены — клиент не выбрал',
  tiles_not_issued: 'плитки не выданы',
  engine_no_match: 'нет подходящей выплаты',
  risk_restricted: 'отклонён по риск-баллу',
  requisite_repeat_limit: 'повтор реквизита',
}

const PAYOUT_GROUP_LABEL: Record<string, string> = {
  unmatched: 'взята, депозитов не было',
  matched_unpaid: 'депозиты были, ни один не оплачен',
}

/** Подпись причины; неизвестный код показывается как есть. */
export function shortfallReasonLabel(side: Side, r: ShortfallReason): string {
  if (side === 'deposit') return DEPOSIT_REASON_LABEL[r.code] ?? v2ReasonLabel(r.code)
  if (r.group === 'rejected') return `не взята: ${r.code ? v2ReasonLabel(r.code) : 'причина не указана'}`
  return PAYOUT_GROUP_LABEL[r.group ?? ''] ?? r.code
}

/** Складывает разбивки нескольких корзин: одна строка на пару «группа + код». */
export function mergeReasons(lists: ShortfallReason[][]): ShortfallReason[] {
  const out: ShortfallReason[] = []
  for (const list of lists) {
    for (const r of list) {
      const hit = out.find((o) => o.group === r.group && o.code === r.code)
      if (hit) {
        hit.count += r.count
        hit.amount += r.amount
      } else {
        out.push({ ...r })
      }
    }
  }
  return out
}

export const money = (amount: number) => `${nf.format(Math.round(amount))} ₽`

/** Axis-friendly money: 2,4 млн / 850 тыс / 400. */
export const moneyShort = (amount: number): string => {
  if (amount >= 1e6) return `${(amount / 1e6).toFixed(1).replace('.', ',')} млн`
  if (amount >= 1e3) return `${Math.round(amount / 1e3)} тыс`
  return String(Math.round(amount))
}

const share = (count: number, total: number) =>
  `${(total > 0 ? (count / total) * 100 : 0).toFixed(1).replace('.', ',')}%`

const escape = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

// Таблица — как разбивка причин в отчётах (`ReportBlock`): те же колонки и цвета темы.
const CELL = 'padding:4px 0 2px;color:var(--bs-body-color)'
const NUM = `${CELL};padding-left:12px;text-align:right;white-space:nowrap`
const HEAD = 'padding-bottom:4px;font-weight:600;color:var(--bs-body-color)'
const ROW = 'border-bottom:1px solid var(--bs-border-color)'

function reasonsTable(section: TooltipSection): string {
  const rows = [...section.reasons]
    .filter((r) => r.count > 0)
    .sort((a, b) => b.count - a.count || b.amount - a.amount)
  if (rows.length === 0) return ''

  const body = rows
    .map(
      (r) =>
        `<tr style="${ROW}">` +
        `<td style="${CELL}">${escape(shortfallReasonLabel(section.side, r))}</td>` +
        `<td style="${NUM}">${nf.format(r.count)}</td>` +
        `<td style="${NUM}">${money(r.amount)}</td>` +
        `<td style="${NUM};color:var(--bs-secondary-color)">${share(r.count, section.total.count)}</td>` +
        `</tr>`
    )
    .join('')

  return (
    `<table style="border-collapse:collapse;width:100%;font-size:12px;margin:2px 0 6px">` +
    `<thead><tr style="${ROW}">` +
    `<th style="${HEAD};text-align:left">Причина</th>` +
    `<th style="${HEAD};text-align:right">Кол-во</th>` +
    `<th style="${HEAD};text-align:right">Сумма</th>` +
    `<th style="${HEAD};text-align:right">Доля</th>` +
    `</tr></thead><tbody>${body}</tbody></table>`
  )
}

function sectionHtml(section: TooltipSection): string {
  const { count, amount } = section.total
  const title = `<div class="apexcharts-tooltip-title">${escape(SIDE_LABEL[section.side])}</div>`
  if (count === 0) {
    return title + `<div style="padding:0 10px 6px;color:var(--bs-secondary-color)">${EMPTY_TEXT[section.side]}</div>`
  }
  return (
    title +
    `<div style="padding:0 10px 6px">` +
    `<div style="font-weight:600;margin-bottom:4px">${nf.format(count)} ${SIDE_UNIT[section.side]} · ${money(amount)}</div>` +
    reasonsTable(section) +
    `</div>`
  )
}

/**
 * Тултип обоих видов графика: заголовок часа, затем по секции на серию — итог
 * (количество и сумма) и под ним таблица причин. Apex такого не умеет, поэтому
 * разметку собираем сами, переиспользуя классы его тултипа ради темы.
 */
export function buildTooltipHtml(title: string, sections: TooltipSection[]): string {
  return (
    `<div class="apexcharts-tooltip-title"><b>${escape(title)}</b></div>` +
    sections.map(sectionHtml).join('')
  )
}
